import { CurrencyPipe, NgForOf, NgIf } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Divider } from 'primeng/divider';
import { UIChart } from 'primeng/chart';
import { SelectButton } from 'primeng/selectbutton';
import {
  DashboardMetricsResponseDto,
  DashboardService,
  OwnerDashboardResponseDto,
  OwnerDashboardUserSummaryDto,
} from '../../../../api';
import { AuthStore } from '../../../../auth/services/auth.store';
import { PageWrapperComponent } from '../../../../shared/pages/page-wrapper/page-wrapper.component';
import { MinutesToHoursPipe } from '../../../../shared/pipes/minutes-to-hours.pipe';
import { DateTimeUtils } from '../../../../shared/utils/date-time-utils';

Chart.register(...registerables, ChartDataLabels);

type DashboardMode = 'team' | 'self';

interface OwnerAggregatedTotals {
  totalDuration: number;
  totalRevenue: number;
  activeUsers: number;
  totalUsers: number;
  averagePerActiveUser: number;
}

@Component({
  selector: 'app-dashboard-page',
  standalone: true,
  imports: [
    PageWrapperComponent,
    Card,
    Divider,
    NgForOf,
    MinutesToHoursPipe,
    CurrencyPipe,
    UIChart,
    NgIf,
    FormsModule,
    Button,
    SelectButton,
    RouterLink,
  ],
  templateUrl: './dashboard-page.component.html',
  styleUrl: './dashboard-page.component.css',
})
export class DashboardPageComponent {
  private readonly authStore = inject(AuthStore);
  private readonly dashboardService = inject(DashboardService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected dashboardData = signal<DashboardMetricsResponseDto>({});
  protected ownerUsers = signal<OwnerDashboardUserSummaryDto[]>([]);
  protected dashboardMode = signal<DashboardMode>('team');

  readonly activeWorkspaceId = computed(() => this.authStore.defaultWorkspace()?.id!);
  readonly isLoggedIn = computed(() => this.authStore.isLoggedIn());
  readonly isOwner = this.authStore.isOwner;
  readonly isOwnerTeamView = computed(() => this.isOwner() && this.dashboardMode() === 'team');
  readonly isOwnerSelfView = computed(() => this.isOwner() && this.dashboardMode() === 'self');
  readonly dashboardModeOptions = [
    { label: 'Moje dane', value: 'self' as DashboardMode },
    { label: 'Zespół', value: 'team' as DashboardMode },
  ];

  readonly currentWeek = DateTimeUtils.getWeekUTCFromDate(new Date());
  readonly currentWeekLabel = computed(() => this.formatWeekLabel(this.currentWeek));

  readonly state = signal<{ loading: boolean; error: any | null }>({
    loading: true,
    error: null,
  });

  readonly ownerTotals = computed<OwnerAggregatedTotals>(() => {
    const users = this.ownerUsers();
    const totalDuration = users.reduce((sum, user) => sum + (user.totalDuration ?? 0), 0);
    const totalRevenue = users.reduce((sum, user) => sum + (user.totalRevenue ?? 0), 0);
    const activeUsers = users.filter((user) => (user.totalDuration ?? 0) > 0).length;

    return {
      totalDuration,
      totalRevenue,
      activeUsers,
      totalUsers: users.length,
      averagePerActiveUser: activeUsers > 0 ? totalDuration / activeUsers : 0,
    };
  });

  readonly collapsedTotalDuration = computed(() =>
    this.isOwnerTeamView()
      ? this.ownerTotals().totalDuration
      : (this.dashboardData().totalPeriod?.totalDuration ?? 0),
  );

  readonly collapsedTotalRevenue = computed(() =>
    this.isOwnerTeamView()
      ? this.ownerTotals().totalRevenue
      : (this.dashboardData().totalPeriod?.totalRevenue ?? 0),
  );

  readonly collapsedSecondaryLabel = computed(() =>
    this.isOwnerTeamView()
      ? `${this.ownerTotals().activeUsers} / ${this.ownerTotals().totalUsers} użytkowników`
      : `${this.dashboardData().topWorkItems?.length ?? 0} pozycji`,
  );

  readonly dashboardTitle = computed(() =>
    this.isOwner()
      ? this.isOwnerTeamView()
        ? 'Dashboard właściciela'
        : 'Dashboard właściciela - moje dane'
      : 'Dashboard',
  );

  readonly dashboardLead = computed(() =>
    this.isOwner()
      ? this.isOwnerTeamView()
        ? 'Szybki podgląd aktywności zespołu w bieżącym tygodniu.'
        : 'Szybki podgląd Twojej aktywności w bieżącym tygodniu.'
      : 'Szybki podgląd Twojej aktywności w bieżącym tygodniu.',
  );

  constructor() {
    effect(() => {
      if (!this.isLoggedIn() || !this.activeWorkspaceId()) {
        return;
      }

      this.fetchDashboard();
    });
  }

  fetchDashboard(): void {
    this.state.update((current) => ({ ...current, loading: true, error: null }));
    const activeWorkspaceId = this.activeWorkspaceId();

    if (!activeWorkspaceId) {
      this.dashboardData.set({});
      this.ownerUsers.set([]);
      this.state.update((current) => ({
        ...current,
        loading: false,
      }));
      return;
    }

    if (this.isOwnerTeamView()) {
      this.loadOwnerDashboard(activeWorkspaceId);
      return;
    }

    this.loadMemberDashboard(activeWorkspaceId);
  }

  chartData = computed(() => {
    const workDates = this.dashboardData().workDates ?? [];
    return {
      labels: workDates.map((d) => {
        const date = new Date(d.workDate!);
        const weekday = date.toLocaleDateString('pl-PL', { weekday: 'short' });
        const dayMonth = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        return `${weekday} ${dayMonth}`;
      }),
      datasets: [
        {
          label: 'Czas pracy (godziny)',
          data: workDates.map((d) => (d.duration ?? 0) / 60),
          backgroundColor: '#4f46e5',
          borderRadius: 6,
          barPercentage: 0.6,
        },
      ],
    };
  });

  chartOptions = computed(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const h = Math.floor(ctx.parsed.y);
              const m = Math.round((ctx.parsed.y - h) * 60);
              return `${h}:${m.toString().padStart(2, '0')} h`;
            },
          },
        },
        datalabels: {
          display: true,
          anchor: 'end',
          align: 'top',
          formatter: (value: number) => {
            if (!value || value === 0) {
              return '';
            }

            const h = Math.floor(value);
            const m = Math.round((value - h) * 60);

            return `${h}:${m.toString().padStart(2, '0')}`;
          },
          color: '#6b7280',
          font: {
            weight: 'bold',
            size: 12,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grace: '20%',
          ticks: {
            stepSize: 1,
            callback: (value: number) => `${value}h`,
          },
          title: {
            display: false,
            text: 'Godziny',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
        },
        x: {
          grid: {
            display: false,
          },
        },
      },
    };
  });

  readonly hasChartData = computed(() =>
    (this.dashboardData().workDates ?? []).some((d) => (d.duration ?? 0) > 0),
  );

  readonly hasTopWorkItems = computed(() =>
    (this.dashboardData().topWorkItems ?? []).some((item) => (item.duration ?? 0) > 0),
  );

  readonly isDashboardEmpty = computed(() => {
    const data = this.dashboardData();
    const hasWorkDatesWithDuration = (data.workDates ?? []).some((d) => (d.duration ?? 0) > 0);
    const hasTopWorkItems = (data.topWorkItems ?? []).some((item) => (item.duration ?? 0) > 0);
    const hasAnyTime = (data.totalPeriod?.totalDuration ?? 0) > 0;
    const hasOwnerUsers = this.ownerUsers().length > 0;

    return this.isOwnerTeamView()
      ? !hasOwnerUsers && !hasWorkDatesWithDuration && !hasTopWorkItems && !hasAnyTime
      : !hasWorkDatesWithDuration && !hasTopWorkItems && !hasAnyTime;
  });

  protected setDashboardMode(mode: DashboardMode): void {
    if (this.dashboardMode() === mode) {
      return;
    }

    this.dashboardMode.set(mode);
    this.fetchDashboard();
  }

  protected navigateToReports(): void {
    this.router.navigate(['/reports']);
  }

  protected navigateToTimesheet(): void {
    this.router.navigate(['/timesheet']);
  }

  protected navigateToWorkspaceUsers(): void {
    const activeWorkspaceId = this.activeWorkspaceId();
    if (!activeWorkspaceId) {
      return;
    }

    void this.router.navigate(['/workspaces', activeWorkspaceId, 'settings'], {
      queryParams: {
        tab: 'users',
      },
    });
  }

  protected getUserShare(user: OwnerDashboardUserSummaryDto): number {
    const totalDuration = this.ownerTotals().totalDuration;
    if (!totalDuration) {
      return 0;
    }

    return ((user.totalDuration ?? 0) / totalDuration) * 100;
  }

  protected formatRole(role: string | undefined): string {
    switch (role) {
      case 'OWNER':
        return 'Właściciel';
      case 'ADMIN':
        return 'Admin';
      default:
        return 'Pracownik';
    }
  }

  protected trackByUserId(_: number, user: OwnerDashboardUserSummaryDto): string {
    return user.id ?? '';
  }

  private loadMemberDashboard(activeWorkspaceId: string): void {
    const range = this.currentWeek;

    this.dashboardService
      .getDashboardData(activeWorkspaceId, {
        startDate: range.start.toISOString(),
        endDate: range.end.toISOString(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: DashboardMetricsResponseDto) => {
          this.dashboardData.set(data);
          this.ownerUsers.set([]);
          this.state.update((current) => ({
            ...current,
            loading: false,
            error: null,
          }));
        },
        error: (error) => {
          console.error('Error fetching dashboard data', error);
          this.state.update((current) => ({
            ...current,
            loading: false,
            error,
          }));
        },
      });
  }

  private loadOwnerDashboard(activeWorkspaceId: string): void {
    const range = this.currentWeek;

    this.dashboardService
      .getOwnerDashboardData(activeWorkspaceId, {
        startDate: range.start.toISOString(),
        endDate: range.end.toISOString(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: OwnerDashboardResponseDto) => {
          this.ownerUsers.set(result.users ?? []);
          this.dashboardData.set(result.dashboard ?? {});
          this.state.update((current) => ({
            ...current,
            loading: false,
            error: null,
          }));
        },
        error: (error) => {
          console.error('Error fetching owner dashboard data', error);
          this.dashboardData.set({});
          this.ownerUsers.set([]);
          this.state.update((current) => ({
            ...current,
            loading: false,
            error,
          }));
        },
      });
  }

  private formatWeekLabel(range: { start: Date; end: Date }): string {
    const startLabel = range.start.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: 'short',
      timeZone: 'UTC',
    });
    const endLabel = range.end.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: 'short',
      timeZone: 'UTC',
    });

    return `${startLabel} - ${endLabel}`;
  }
}
